import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocketContext } from '../contexts/WebSocketProvider';

export default function useTotalEnergy(range = null) {
  const [totalEnergy, setTotalEnergy] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { send, isConnected, registerHandler } = useWebSocketContext();
  const mountedRef = useRef(true);
  const groupId = localStorage.getItem("group_id");

  // Compute default 30-day range
  const defaultRange = useCallback(() => {
    const today = new Date();
    const past30 = new Date();
    past30.setDate(today.getDate() - 29); // includes today as last day
    const format = (d) => d.toISOString().split('T')[0];
    return { fromDate: format(past30), toDate: format(today) };
  }, []);

  const fetchTotalEnergy = useCallback(async () => {
    if (!mountedRef.current) return;

    if (!isConnected) {
      setError("WebSocket not connected");
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const appliedRange = range?.fromDate && range?.toDate ? range : defaultRange();

      const payload = {
        group_id: groupId?.toString(),
        start_date: appliedRange.fromDate,
        end_date: appliedRange.toDate,
      };

      console.log("Payload being sent:", payload);

      const response = await send(
        'cms_get_total_energy_consumed_by_group_id',
        payload
      );

      if (!response || response.type !== 'cms_get_total_energy_consumed_by_group_id_response') {
        throw new Error('Invalid response from server');
      }

      if (response.data === undefined) {
        throw new Error('Server did not return total energy');
      }

      setTotalEnergy(response.data);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [isConnected, send, groupId, range, defaultRange]);

  // Subscribe to live updates
  useEffect(() => {
    const unregister = registerHandler('total_energy_update', (msg) => {
      if (msg.totalEnergy !== undefined && mountedRef.current) {
        setTotalEnergy(msg.totalEnergy);
      }
    });
    return () => unregister();
  }, [registerHandler]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchTotalEnergy();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchTotalEnergy]);

  return { totalEnergy, isLoading, error, refetch: fetchTotalEnergy };
}
